/**
 * What an app request looks like while it is crossing the channel.
 *
 * The phone has no backend. What it has is a channel to the desktop, and the
 * desktop has one. So a call from the app becomes an envelope: method, path,
 * headers, body. The desktop opens it, asks its own backend on loopback, and
 * sends the answer back in the same shape.
 *
 * Nine kinds of frame, and one group of them is the whole streaming story:
 *
 *   ask         the request
 *   head        the answer's status and headers, before any of its body
 *   chunk       a piece of the body
 *   end         no more pieces
 *   fail        it did not happen, and why
 *   pairing     the desktop offering to sign this phone in
 *   rendezvous  where to meet again if this channel dies
 *   ping/pong   still there?
 *
 * A council answer that arrives token by token and a settings page that
 * arrives at once are the same path: head, chunks, end. There is no separate
 * streaming mode to get wrong.
 *
 * The guards here are the security boundary. A paired phone can put anything
 * it likes in an envelope, and on the other side sits a machine that can
 * reach a home network. So a path that is not /api is refused, an address the
 * phone invented is refused, and a header nobody needs is dropped rather than
 * passed along.
 */

export const TUNNEL_PROTOCOL = "novamund-tunnel/1";

/** The kinds of frame that may cross. Anything else is a bug. */
export const FRAME_KINDS = [
  "ask", "head", "chunk", "end", "fail", "pairing", "rendezvous", "ping", "pong",
  // Fix afscheid (15 oktober 2026): de telefoon zegt dat hij weggaat.
  "bye",
];

/** What the app is allowed to ask for. Nothing here writes to a filesystem. */
export const METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE"];

/**
 * The only headers that travel. Everything else a browser or a library adds
 * is either meaningless on the other side or a way to say something the app
 * never meant to say.
 */
export const HEADERS_ALLOWED = ["authorization", "content-type", "accept"];

/** A data channel message has a ceiling; bodies are cut to fit before this. */
export const MAX_FRAME_BYTES = 48 * 1024;

export class TunnelError extends Error {}

/**
 * Whether this is a request the desktop may carry out.
 *
 * Kept as its own function, and called on both sides. The phone refuses to
 * send it and the desktop refuses to do it, because "the other side already
 * checked" is how a check ends up not happening at all.
 */
export function checkAsk({ method, path }) {
  if (!METHODS.includes(method)) {
    throw new TunnelError(`Refusing to send a ${method} through the tunnel.`);
  }
  if (typeof path !== "string" || !path.startsWith("/api")) {
    // The tunnel carries this app's own api and nothing else. Without this a
    // paired phone could name any address the desktop can reach, including
    // the ones inside a home network.
    throw new TunnelError("Refusing a path outside the api.");
  }
  if (path.includes("://") || path.startsWith("//") || path.includes("..")) {
    throw new TunnelError("Refusing an address rather than a path.");
  }
  return true;
}

/** Drops everything that is not on the list, and lowercases what is left. */
export function safeHeaders(headers = {}) {
  const kept = {};
  Object.keys(headers || {}).forEach((name) => {
    const key = String(name).toLowerCase();
    if (HEADERS_ALLOWED.includes(key) && headers[name] != null) {
      kept[key] = String(headers[name]);
    }
  });
  return kept;
}

export function encodeFrame(frame) {
  if (!FRAME_KINDS.includes(frame?.kind)) {
    throw new TunnelError(`Refusing to send "${frame?.kind}" through the tunnel.`);
  }
  if (!frame.id) throw new TunnelError("A frame needs the request it belongs to.");
  if (frame.kind === "ask") {
    checkAsk(frame);
    return JSON.stringify({
      v: TUNNEL_PROTOCOL,
      kind: "ask",
      id: frame.id,
      method: frame.method,
      path: frame.path,
      headers: safeHeaders(frame.headers),
      body: frame.body ?? null,
    });
  }
  if (frame.kind === "pairing") {
    // The desktop offering to sign this phone in. Carries only what the
    // pairing routes need; the phone's key is made on the phone and its
    // private half never travels.
    return JSON.stringify({
      v: TUNNEL_PROTOCOL,
      kind: "pairing",
      id: frame.id,
      secret: frame.secret,
    });
  }
  if (frame.kind === "rendezvous") {
    // Where to meet again. Sent by the desktop while the channel is up, so
    // that when the channel dies there is already somewhere to go back to and
    // the owner is never asked to walk to the other room and scan again.
    //
    // It is a room name and a one-time secret and nothing else: no token, no
    // key, nothing that would be worth having on its own. Whoever held it
    // could ask to be introduced to that desktop, and would then have to get
    // through pairing like anybody else.
    return JSON.stringify({
      v: TUNNEL_PROTOCOL,
      kind: "rendezvous",
      id: frame.id,
      secret: frame.secret,
    });
  }
  if (frame.kind === "ping" || frame.kind === "pong" || frame.kind === "bye") {
    // Nothing but its own name. A channel with no traffic on it is
    // indistinguishable from a dead one until something is sent.
    return JSON.stringify({ v: TUNNEL_PROTOCOL, kind: frame.kind, id: frame.id });
  }
  if (frame.kind === "head") {
    // The answer's own headers, kept. The content type is how the app knows
    // whether it is holding json or text, and dropping it turned every
    // answer into a string that looked like data but was not.
    return JSON.stringify({
      v: TUNNEL_PROTOCOL,
      kind: "head",
      id: frame.id,
      status: frame.status,
      headers: frame.headers || {},
    });
  }
  // chunk, end, fail: nothing but what they carry. Written out per kind
  // rather than spread, so a field can never travel by accident.
  return JSON.stringify({
    v: TUNNEL_PROTOCOL,
    kind: frame.kind,
    id: frame.id,
    ...(frame.kind === "chunk" ? { data: frame.data ?? "" } : {}),
    ...(frame.kind === "fail" ? { reason: frame.reason || "" } : {}),
  });
}

export function decodeFrame(raw) {
  let frame;
  try {
    frame = JSON.parse(raw);
  } catch {
    throw new TunnelError("That was not a frame.");
  }
  if (frame?.v !== TUNNEL_PROTOCOL) {
    throw new TunnelError("That frame is from another version of this protocol.");
  }
  if (!FRAME_KINDS.includes(frame.kind)) {
    throw new TunnelError(`Unknown frame "${frame.kind}".`);
  }
  if (!frame.id) throw new TunnelError("A frame needs the request it belongs to.");
  if (frame.kind === "ask") checkAsk(frame);
  return frame;
}

/**
 * Cuts a body into pieces that fit through the channel.
 *
 * Whole answers and streamed ones are the same thing here: a list of pieces.
 * A body that is already small is one piece, not a special case.
 */
export function cut(text, size = MAX_FRAME_BYTES) {
  if (!text) return [];
  const pieces = [];
  for (let at = 0; at < text.length; at += size) pieces.push(text.slice(at, at + size));
  return pieces;
}
